import { useEffect, useState } from 'react';

import { useSelector, useDispatch } from 'react-redux';

import { LoadingStatus } from 'src/constants/loading-status';
import { useStatsigGate } from 'src/contexts/feature-provider';
import {
  fetchJiraIssueCreationMetadata,
  onPrUnsupportedFieldsError,
  onUnsupportedFieldsError,
} from 'src/redux/jira/actions';
import { getIssueMeta } from 'src/redux/jira/selectors/jira-issue-selectors';
import { Project, IssueType, Site } from 'src/redux/jira/types';
import { BucketState } from 'src/types/state';

export const useIssueMeta = (
  selectedProject?: Project,
  commentId?: number,
  selectedSite?: Site
) => {
  const dispatch = useDispatch();
  const [selectedIssueType, setSelectedIssueType] = useState<
    IssueType | undefined
  >();
  const handleSetSelectedIssueType = (issueType: undefined | IssueType) => {
    setSelectedIssueType(issueType);
    // This is for checking if any of the fields are
    // unsupported for the selected issue type
    if (issueType && commentId) {
      dispatch(onPrUnsupportedFieldsError({ issueType, commentId }));
    } else if (issueType) {
      dispatch(onUnsupportedFieldsError({ issueType }));
    }
  };

  const isNewIssueMetaEnabled = useStatsigGate('bbc-jira-issue-selector');

  const oldIssueMeta = useSelector<
    BucketState,
    { issueTypes: IssueType[]; fetchedStatus: LoadingStatus }
  >(state => {
    if (selectedProject) {
      const { issueCreationMetadata } = state.jira.createJiraIssue;
      const metadataForSiteAndProject = (issueCreationMetadata[
        selectedProject.site.cloudId
      ] || {})[selectedProject.id];

      if (metadataForSiteAndProject) {
        return metadataForSiteAndProject;
      }
    }

    return {
      issueTypes: [],
      fetchedStatus: LoadingStatus.Before,
    };
  });

  const newIssueMeta = useSelector((state: BucketState) =>
    getIssueMeta(state, selectedProject)
  );

  const issueMeta = isNewIssueMetaEnabled ? newIssueMeta : oldIssueMeta;

  useEffect(() => {
    if (selectedProject && issueMeta.fetchedStatus === LoadingStatus.Before) {
      dispatch(
        fetchJiraIssueCreationMetadata({
          cloudId: selectedProject.site.cloudId,
          projectId: selectedProject.id,
        })
      );
    }
  }, [selectedProject, issueMeta.fetchedStatus, dispatch]);

  useEffect(
    () => {
      if (
        issueMeta.fetchedStatus === LoadingStatus.Success &&
        issueMeta.issueTypes.length > 0
      ) {
        handleSetSelectedIssueType(issueMeta.issueTypes[0]);
      } else {
        handleSetSelectedIssueType(undefined);
      }
    },
    // "handleSetSelectedIssueType" is a nested function and a new function gets created on
    // each render, if we add it to list of deps, this effect will get called in an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSite, selectedProject, issueMeta]
  );
  return { issueMeta, selectedIssueType, handleSetSelectedIssueType };
};
